import { useEffect } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  getApplicationComments,
  addApplicationComment,
  updateApplicationComment,
  deleteApplicationComment,
  toggleApplicationCommentReaction,
} from "@/modules/recruitment/applications/comments-service"
import {
  ApplicationComment,
  ApplicationCommentDeleted,
  ApplicationCommentReactionEmoji,
  ApplicationCommentReactionSummary,
} from "@/modules/recruitment/applications/dtos/ApplicationComment"
import { useApplicationHub } from "@/modules/recruitment/applications/hooks/useApplicationHub"

const commentsQueryKey = (organizationId: string, applicationId: string) => ["application-comments", organizationId, applicationId]

function replaceReactions(
  comments: ApplicationComment[],
  summary: ApplicationCommentReactionSummary,
): ApplicationComment[] {
  return comments.map((comment) => {
    if (comment.id === summary.commentId) {
      return { ...comment, reactions: summary.reactions }
    }
    if (comment.replies.length > 0) {
      return { ...comment, replies: replaceReactions(comment.replies, summary) }
    }
    return comment
  })
}

function upsertComment(comments: ApplicationComment[], incoming: ApplicationComment): ApplicationComment[] {
  if (incoming.parentCommentId) {
    return comments.map((comment) => {
      if (comment.id !== incoming.parentCommentId) return comment
      const alreadyPresent = comment.replies.some((r) => r.id === incoming.id)
      return {
        ...comment,
        replies: alreadyPresent
          ? comment.replies.map((r) => (r.id === incoming.id ? incoming : r))
          : [...comment.replies, incoming],
      }
    })
  }

  const alreadyPresent = comments.some((c) => c.id === incoming.id)
  if (alreadyPresent) {
    return comments.map((c) => (c.id === incoming.id ? incoming : c))
  }
  return [incoming, ...comments]
}

function markCommentDeleted(comments: ApplicationComment[], payload: ApplicationCommentDeleted): ApplicationComment[] {
  return comments.map((comment) => {
    if (comment.id === payload.commentId) {
      return { ...comment, isDeleted: true }
    }
    if (comment.replies.length > 0) {
      return { ...comment, replies: markCommentDeleted(comment.replies, payload) }
    }
    return comment
  })
}

export function useApplicationComments(organizationId: string | undefined, applicationId: string | undefined) {
  return useQuery({
    queryKey: commentsQueryKey(organizationId ?? "", applicationId ?? ""),
    queryFn: () => getApplicationComments(organizationId!, applicationId!),
    enabled: !!organizationId && !!applicationId,
  })
}

export function useAddApplicationComment(organizationId: string, applicationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ text, parentCommentId }: { text: string; parentCommentId?: string }) =>
      addApplicationComment(organizationId, applicationId, text, parentCommentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentsQueryKey(organizationId, applicationId) })
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to post comment")
    },
  })
}

export function useUpdateApplicationComment(organizationId: string, applicationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId, text }: { commentId: string; text: string }) =>
      updateApplicationComment(organizationId, applicationId, commentId, text),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentsQueryKey(organizationId, applicationId) })
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to update comment")
    },
  })
}

export function useDeleteApplicationComment(organizationId: string, applicationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId }: { commentId: string }) =>
      deleteApplicationComment(organizationId, applicationId, commentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: commentsQueryKey(organizationId, applicationId) })
      toast.success("Comment deleted")
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete comment")
    },
  })
}

export function useToggleApplicationCommentReaction(organizationId: string, applicationId: string) {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: ({ commentId, emoji }: { commentId: string; emoji: ApplicationCommentReactionEmoji }) =>
      toggleApplicationCommentReaction(organizationId, applicationId, commentId, emoji),
    onSuccess: (summary) => {
      queryClient.setQueryData<ApplicationComment[]>(commentsQueryKey(organizationId, applicationId), (current) =>
        current ? replaceReactions(current, summary) : current,
      )
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to react to comment")
    },
  })
}

export function useApplicationCommentsLiveSync(organizationId: string, applicationId: string) {
  const queryClient = useQueryClient()
  const { onCommentPosted, onCommentUpdated, onCommentDeleted, onCommentReactionChanged } = useApplicationHub()

  useEffect(() => {
    const unsubscribePosted = onCommentPosted((comment) => {
      if (comment.applicationId !== applicationId) return
      queryClient.setQueryData<ApplicationComment[]>(commentsQueryKey(organizationId, applicationId), (current) =>
        current ? upsertComment(current, comment) : current,
      )
    })

    const unsubscribeUpdated = onCommentUpdated((comment) => {
      if (comment.applicationId !== applicationId) return
      queryClient.setQueryData<ApplicationComment[]>(commentsQueryKey(organizationId, applicationId), (current) =>
        current ? upsertComment(current, comment) : current,
      )
    })

    const unsubscribeDeleted = onCommentDeleted((payload) => {
      queryClient.setQueryData<ApplicationComment[]>(commentsQueryKey(organizationId, applicationId), (current) =>
        current ? markCommentDeleted(current, payload) : current,
      )
    })

    const unsubscribeReaction = onCommentReactionChanged((summary) => {
      queryClient.setQueryData<ApplicationComment[]>(commentsQueryKey(organizationId, applicationId), (current) =>
        current ? replaceReactions(current, summary) : current,
      )
    })

    return () => {
      unsubscribePosted()
      unsubscribeUpdated()
      unsubscribeDeleted()
      unsubscribeReaction()
    }
  }, [organizationId, applicationId, onCommentPosted, onCommentUpdated, onCommentDeleted, onCommentReactionChanged, queryClient])
}
